import { type FunctionComponent, useMemo, useState } from 'react';
import {
  EuiButton,
  EuiButtonEmpty,
  EuiComboBox,
  type EuiComboBoxOptionOption,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutHeader,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiForm,
  EuiFormRow,
  EuiText,
  EuiTitle,
  useGeneratedHtmlId,
  EuiSpacer,
  EuiToolTip,
} from '@elastic/eui';
import type { Person, Group } from '@domain/person';
import useGroups from '@data/groups';
import { useAddToGroup } from '@data/group-mutations';
import { compareStrings } from '@lib/utils';

interface PersonWithGroups extends Person {
  groups: Group[];
}

interface AddGroupProps {
  person: PersonWithGroups;
}

type GroupOption = EuiComboBoxOptionOption<Group>;

const groupOption = (group: Group): GroupOption => ({
  label: group.name,
  value: group,
});

const renderOption = (option: GroupOption) => {
  const value = option.value as Group;
  if (!value.name) return;

  return (
    <div>
      <EuiText size="xs">{value.name}</EuiText>
      <EuiText size="xs" color="subdued">
        {value.description}
      </EuiText>
    </div>
  );
};

const AddGroup: FunctionComponent<AddGroupProps> = props => {
  const { person } = props;
  const { groups: allGroups = [] } = useGroups();
  const [addToGroup, { loading: mutating }] = useAddToGroup();

  const titleId = useGeneratedHtmlId({ prefix: 'add-group' });

  const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<Group[]>([]);

  const availableGroups = useMemo(
    () =>
      allGroups
        .filter(group => !person.groups.some(g => g.name === group.name))
        .sort((a, b) =>
          compareStrings(a.name.toLowerCase(), b.name.toLowerCase())
        ),
    [allGroups, person.groups]
  );

  const openFlyout = () => {
    setIsFlyoutVisible(true);
  };

  const closeFlyout = () => {
    setIsFlyoutVisible(false);
    setSelectedGroups([]);
  };

  const handleClick = () => {
    Promise.all(
      selectedGroups.map(g => addToGroup(person.idnumber, g.name))
    ).then(() => {
      closeFlyout();
    });
  };

  return (
    <>
      <EuiToolTip content="Add new group permissions">
        <EuiButton iconType="plus" onClick={openFlyout}>
          Add
        </EuiButton>
      </EuiToolTip>
      {isFlyoutVisible && (
        <EuiFlyout
          onClose={closeFlyout}
          ownFocus
          size="s"
          aria-labelledby={titleId}>
          <EuiFlyoutHeader hasBorder>
            <EuiTitle size="m">
              <h3 id={titleId}>Grant groups</h3>
            </EuiTitle>
          </EuiFlyoutHeader>
          <EuiFlyoutBody>
            <EuiText>
              <p>
                Granting group permissions to {person.firstname}{' '}
                {person.surname}
              </p>
            </EuiText>
            <EuiSpacer size="m" />
            <EuiForm>
              <EuiFormRow label="Choose groups">
                <EuiComboBox
                  fullWidth
                  selectedOptions={selectedGroups.map(groupOption)}
                  options={availableGroups.map(groupOption)}
                  onChange={options => {
                    setSelectedGroups(
                      options.map(option => option.value as Group)
                    );
                  }}
                  renderOption={renderOption}
                  rowHeight={50}
                  isClearable={true}
                  isDisabled={mutating}
                />
              </EuiFormRow>
            </EuiForm>
          </EuiFlyoutBody>
          <EuiFlyoutFooter>
            <EuiFlexGroup justifyContent="spaceBetween">
              <EuiFlexItem grow={false}>
                <EuiButtonEmpty
                  iconType="cross"
                  onClick={closeFlyout}
                  flush="left"
                  isLoading={mutating}>
                  Cancel
                </EuiButtonEmpty>
              </EuiFlexItem>
              <EuiFlexItem grow={false}>
                <EuiButton
                  iconType="plus"
                  onClick={handleClick}
                  fill
                  isDisabled={selectedGroups.length === 0}
                  isLoading={mutating}>
                  Add
                </EuiButton>
              </EuiFlexItem>
            </EuiFlexGroup>
          </EuiFlyoutFooter>
        </EuiFlyout>
      )}
    </>
  );
};

export default AddGroup;
